/**
 * @file   MatchDemandPanel.js
 * @brief  Orchestrates the demand insight display on Match tab.
 *         Uses detectTier() to determine what to show based on match count:
 *         Tier 1: histogram + slider + stats + insight
 *         Tier 2: stats strip + insight
 *         Tier 3-4: LowDataState
 */
import React, { useState, useMemo, useRef } from "react";
import { detectTier } from "@/helpers/matchDemandUtils";
import { calculatePropertyPriceRange } from "@/helpers/priceCapUtils";
import { BUY, LONG_RENT } from "@/constants/common";
import DemandHistogram from "../DemandHistogram";
import SinglePriceSlider from "../SinglePriceSlider";
import QuickStatsStrip from "../QuickStatsStrip";
import InsightCallout from "../InsightCallout";
import ReportCTA from "../../../Common/ReportCTA";
import LowDataState from "../LowDataState";
import "./match_demand_panel.scss";

/**
 * Extract prices from match results for histogram computation.
 * Uses average of Budget_mn/Budget_mx when both exist.
 */
const extractPrices = (adList) => {
  if (!adList || adList.length === 0) return [];
  return adList
    .map((ad) => {
      const mn = ad.Budget_mn;
      const mx = ad.Budget_mx;
      if (mn && mx) return (mn + mx) / 2;
      if (mn) return mn;
      if (mx) return mx;
      return null;
    })
    .filter((p) => p != null && p > 0);
};

const DEBOUNCE_MS = 400;

const MatchDemandPanel = ({
  selectedProperty,
  adType,
  totalCountMatch,
  adListGitchaGenie,
  onPricePointChange,
}) => {
  const [sliderPrice, setSliderPrice] = useState(null);
  const [confirmedPrice, setConfirmedPrice] = useState(null);
  const debounceRef = useRef(null);

  const handleSliderChange = (price) => {
    setSliderPrice(price);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setConfirmedPrice(price);
      onPricePointChange?.(price);
    }, DEBOUNCE_MS);
  };

  const matchCount = totalCountMatch || 0;
  const tier = detectTier(matchCount);
  // The report's own eligible count (from getInventoryListByUserId's
  // reportMatchCount overlay), not matchCount above - a different counting
  // mechanism entirely (the demand panel's own AI-genie match fetch). Gating
  // ReportCTA on tier/matchCount meant REPORT_MIN_MATCHES was
  // unreachable dead code behind detectTier's stricter 5+ cutoff: a property
  // could clear the report's own threshold while this panel's tier logic
  // still called it "low data" and never rendered the button at all.
  const reportMatchCount = selectedProperty?.reportMatchCount ?? 0;

  // Get asking price from selected property
  const askingPrice = useMemo(() => {
    if (!selectedProperty) return 0;
    if (adType === BUY) return selectedProperty.AskingPrice_Sell || 0;
    if (adType === LONG_RENT) return selectedProperty.AskingPrice_Rent || 0;
    return selectedProperty.AskingPrice_Night || 0;
  }, [selectedProperty, adType]);

  // Calculate slider range from property price
  const priceRange = useMemo(
    () => calculatePropertyPriceRange(askingPrice, adType),
    [askingPrice, adType],
  );

  // Extract match prices for histogram
  const prices = useMemo(
    () => extractPrices(adListGitchaGenie),
    [adListGitchaGenie],
  );

  const rangeMin = priceRange?.min || 0;
  const rangeMax = priceRange?.max || 0;

  // Reset slider when property changes
  const propertyId = selectedProperty?.Inv_Id;
  useMemo(() => {
    setSliderPrice(null);
    setConfirmedPrice(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [propertyId]);

  // Tier 3-4: low data states
  let tierBody;
  if (tier >= 3) {
    tierBody = <LowDataState tier={tier} matchCount={matchCount} />;
  } else if (tier === 2) {
    // Tier 2: simplified - stats + insight, no histogram
    tierBody = (
      <div className="match-demand-panel">
        <QuickStatsStrip
          matchCount={matchCount}
          prices={prices}
          adType={adType}
          rangeMax={rangeMax}
        />
        <InsightCallout
          askingPrice={askingPrice}
          sliderPrice={null}
          prices={prices}
          matchCount={matchCount}
          adType={adType}
        />
      </div>
    );
  } else {
    // Tier 1: full experience
    tierBody = (
      <div className="match-demand-panel">
        <QuickStatsStrip
          matchCount={matchCount}
          prices={prices}
          adType={adType}
          rangeMax={rangeMax}
        />
        {rangeMax > 0 && (
          <>
            <DemandHistogram
              prices={prices}
              adType={adType}
              askingPrice={askingPrice}
              sliderPrice={sliderPrice}
              rangeMin={rangeMin}
              rangeMax={rangeMax}
            />
            <SinglePriceSlider
              value={sliderPrice ?? askingPrice}
              onChange={handleSliderChange}
              min={rangeMin}
              max={rangeMax}
              step={priceRange?.step}
              askingPrice={askingPrice}
            />
          </>
        )}
        <InsightCallout
          askingPrice={askingPrice}
          sliderPrice={confirmedPrice}
          prices={prices}
          matchCount={matchCount}
          adType={adType}
        />
      </div>
    );
  }

  return (
    <>
      {tierBody}
      {adType === BUY && propertyId && (
        <ReportCTA inventoryId={propertyId} totalMatches={reportMatchCount} />
      )}
    </>
  );
};

export default MatchDemandPanel;
